import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindCertificateRequestsForSupplierQueryResponse,
  FindCertificateRequestsForSupplierQueryParams,
} from "../types/FindCertificateRequestsForSupplier"

type FindCertificateRequestsForSupplierClient = typeof client<
  FindCertificateRequestsForSupplierQueryResponse,
  Error,
  never
>
type FindCertificateRequestsForSupplier = {
  data: FindCertificateRequestsForSupplierQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindCertificateRequestsForSupplierQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindCertificateRequestsForSupplierClient>>
  client: {
    parameters: Partial<Parameters<FindCertificateRequestsForSupplierClient>[0]>
    return: Awaited<ReturnType<FindCertificateRequestsForSupplierClient>>
  }
}
export const findCertificateRequestsForSupplierQueryKey = (
  params?: FindCertificateRequestsForSupplier["queryParams"]
) =>
  [
    { url: "/v1/certificate-request/find-certificate-requests-for-supplier" },
    ...(params ? [params] : []),
  ] as const
export type FindCertificateRequestsForSupplierQueryKey = ReturnType<
  typeof findCertificateRequestsForSupplierQueryKey
>
export function findCertificateRequestsForSupplierQueryOptions<
  TData = FindCertificateRequestsForSupplier["response"],
  TQueryData = FindCertificateRequestsForSupplier["response"],
>(
  params?: FindCertificateRequestsForSupplier["queryParams"],
  options: FindCertificateRequestsForSupplier["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindCertificateRequestsForSupplier["response"],
    FindCertificateRequestsForSupplier["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findCertificateRequestsForSupplierQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindCertificateRequestsForSupplier["data"],
        FindCertificateRequestsForSupplier["error"]
      >({
        method: "get",
        url: `/v1/certificate-request/find-certificate-requests-for-supplier`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find certificate requests for supplier
 * @link /v1/certificate-request/find-certificate-requests-for-supplier
 */
export function useFindCertificateRequestsForSupplier<
  TData = FindCertificateRequestsForSupplier["response"],
  TQueryData = FindCertificateRequestsForSupplier["response"],
  TQueryKey extends QueryKey = FindCertificateRequestsForSupplierQueryKey,
>(
  params?: FindCertificateRequestsForSupplier["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindCertificateRequestsForSupplier["response"],
        FindCertificateRequestsForSupplier["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindCertificateRequestsForSupplier["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindCertificateRequestsForSupplier["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findCertificateRequestsForSupplierQueryKey(params)
  const query = useQuery<
    FindCertificateRequestsForSupplier["data"],
    FindCertificateRequestsForSupplier["error"],
    TData,
    any
  >({
    ...findCertificateRequestsForSupplierQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindCertificateRequestsForSupplier["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
